import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import colorsDark from '../../assets/colors/dark';

const PlaybackDebugMonitor = ({ video, debugLog }) => {
  const { t } = useTranslation();

  const isGateway = video?.type === 'on_gateway';
  const isDvr = video?.type === 'on_dvr';
  const isS3Played = debugLog?.s3Link && debugLog?.s3Link?.status !== 'skipped';

  if (!isGateway && !isDvr && !isS3Played) {
    return null;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return colorsDark.$online;
      case 'error':
        return colorsDark.$errors;
      case 'warning':
      case 'triggered':
        return colorsDark.$warning;
      case 'pending':
      case 'loading':
        return '#028fd5';
      case 'skipped':
      default:
        return colorsDark.$offline;
    }
  };

  const getStatusIcon = (status) => {
    const color = getStatusColor(status);
    switch (status) {
      case 'success':
        return <CheckCircleFilled style={{ color, marginRight: 8 }} />;
      case 'error':
        return <CloseCircleFilled style={{ color, marginRight: 8 }} />;
      case 'warning':
      case 'triggered':
        return <ExclamationCircleFilled style={{ color, marginRight: 8 }} />;
      case 'pending':
      case 'loading':
        return <LoadingOutlined style={{ color, marginRight: 8 }} />;
      case 'skipped':
      default:
        return <MinusCircleOutlined style={{ color, marginRight: 8 }} />;
    }
  };

  const renderStepItem = (titleConfig, stepData) => {
    const { status, error, text } = stepData || { status: 'skipped' };
    const color = getStatusColor(status);
    let titleText = '';
    if (typeof titleConfig === 'string') {
      titleText = titleConfig;
    } else if (titleConfig && typeof titleConfig === 'object') {
      if (status === 'loading' && text) {
        titleText = titleConfig.prefix
          ? `${titleConfig.prefix}: ${text}`
          : titleConfig[status] || text;
      } else {
        titleText =
          titleConfig[status] ||
          titleConfig.default ||
          titleConfig.pending ||
          text ||
          '';
      }
    }

    return (
      <div style={{ marginBottom: 6, padding: '2px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {getStatusIcon(status)}
          <span
            style={{
              fontWeight: 600,
              color: status === 'skipped' ? colorsDark.$offline : color,
            }}
          >
            {t(titleText)}
          </span>
        </div>
        {status === 'error' && error && (
          <div
            style={{
              marginLeft: 24,
              fontSize: 12,
              color: colorsDark.$errors,
              marginTop: 2,
            }}
          >
            {t('Error')}: {error}
          </div>
        )}
      </div>
    );
  };

  if (isGateway) {
    const s1 = debugLog?.gatewayCheck || { status: 'success' };
    const s2 = debugLog?.gatewayStream || { status: 'pending' };
    const s3 = debugLog?.gatewayPlayback || { status: 'pending' };
    const s4_fb = debugLog?.transcodeFallback;
    const s5_req = debugLog?.transcodeRequest;
    const s6_conn = debugLog?.transcodeConnect;

    const showS2 = s1.status === 'success';
    const showS3 = showS2 && (s2.status === 'success' || s3.status !== 'skipped');

    return (
      <div className="debug-monitor-section" style={{ paddingTop: 4 }}>
        {renderStepItem(
          {
            success: 'Step 1: Verified local gateway record',
            pending: 'Step 1: Checking gateway record...',
            error: 'Step 1: Gateway record verification failed',
          },
          s1,
        )}
        {showS2 &&
          renderStepItem(
            {
              prefix: 'Step 2',
              loading: 'Step 2: Requesting gateway stream...',
              pending: 'Step 2: Requesting gateway stream...',
              success: 'Step 2: Gateway stream link received',
              error: 'Step 2: Failed to request gateway stream',
            },
            s2,
          )}
        {showS3 &&
          renderStepItem(
            {
              prefix: 'Step 3',
              loading: 'Step 3: Connecting to gateway stream...',
              pending: 'Step 3: Connecting to gateway stream...',
              success: 'Step 3: Gateway stream playing',
              error: 'Step 3: Gateway stream playback failed',
            },
            s3,
          )}
        {s4_fb && (
          <>
            {renderStepItem(
              'Step 4: Switching to compatible stream (H.264)',
              s4_fb,
            )}
            {s5_req &&
              renderStepItem(
                {
                  prefix: 'Step 5',
                  loading: 'Step 5: Requesting transcode stream...',
                  pending: 'Step 5: Requesting transcode stream...',
                  success: 'Step 5: Transcode stream link received',
                  error: 'Step 5: Failed to request transcode stream',
                },
                s5_req,
              )}
            {s6_conn &&
              renderStepItem(
                {
                  prefix: 'Step 6',
                  loading: 'Step 6: Connecting transcode stream...',
                  pending: 'Step 6: Connecting transcode stream...',
                  success: 'Step 6: Transcode playback ready',
                  error: 'Step 6: Transcode playback failed',
                },
                s6_conn,
              )}
          </>
        )}
      </div>
    );
  }

  if (isDvr) {
    const s1 = debugLog?.dvrCheck || { status: 'success' };
    const s2 = debugLog?.dvrStream || { status: 'pending' };
    const s3 = debugLog?.dvrPlayback || { status: 'pending' };
    const s4_fb = debugLog?.transcodeFallback;
    const s5_req = debugLog?.transcodeRequest;
    const s6_conn = debugLog?.transcodeConnect;

    const showS2 = s1.status === 'success';
    const showS3 = showS2 && (s2.status === 'success' || s3.status !== 'skipped');

    return (
      <div className="debug-monitor-section" style={{ paddingTop: 4 }}>
        {renderStepItem(
          {
            success: 'Step 1: Verified local DVR record',
            pending: 'Step 1: Checking DVR record...',
            error: 'Step 1: DVR record verification failed',
          },
          s1,
        )}
        {showS2 &&
          renderStepItem(
            {
              prefix: 'Step 2',
              loading: 'Step 2: Requesting DVR stream...',
              pending: 'Step 2: Requesting DVR stream...',
              success: 'Step 2: DVR stream link received',
              error: 'Step 2: Failed to request DVR stream',
            },
            s2,
          )}
        {showS3 &&
          renderStepItem(
            {
              prefix: 'Step 3',
              loading: 'Step 3: Connecting to DVR stream...',
              pending: 'Step 3: Connecting to DVR stream...',
              success: 'Step 3: DVR stream playing',
              error: 'Step 3: DVR stream playback failed',
            },
            s3,
          )}
        {s4_fb && (
          <>
            {renderStepItem(
              'Step 4: Switching to compatible stream (H.264)',
              s4_fb,
            )}
            {s5_req &&
              renderStepItem(
                {
                  prefix: 'Step 5',
                  loading: 'Step 5: Requesting transcode stream...',
                  pending: 'Step 5: Requesting transcode stream...',
                  success: 'Step 5: Transcode stream link received',
                  error: 'Step 5: Failed to request transcode stream',
                },
                s5_req,
              )}
            {s6_conn &&
              renderStepItem(
                {
                  prefix: 'Step 6',
                  loading: 'Step 6: Connecting transcode stream...',
                  pending: 'Step 6: Connecting transcode stream...',
                  success: 'Step 6: Transcode playback ready',
                  error: 'Step 6: Transcode playback failed',
                },
                s6_conn,
              )}
          </>
        )}
      </div>
    );
  }

  const s1 = debugLog?.s3Link || { status: 'skipped' };
  const s2 = debugLog?.s3Playback || { status: 'skipped' };
  const s3 = debugLog?.fallback || { status: 'skipped' };
  const s4_req = debugLog?.streamRequest || debugLog?.gatewayStream || { status: 'skipped' };
  const s4_conn = debugLog?.streamConnect || debugLog?.gatewayPlayback || { status: 'skipped' };
  const s5_fb = debugLog?.transcodeFallback;
  const s6_req = debugLog?.transcodeRequest;
  const s6_conn = debugLog?.transcodeConnect;

  const isFallbackTriggered =
    s2.status === 'error' ||
    s3.status === 'triggered' ||
    s4_req.status !== 'skipped';

  const showS2 = s1.status === 'success';
  const isS3Success = showS2 && s2.status === 'success' && !isFallbackTriggered;

  return (
    <div className="debug-monitor-section" style={{ paddingTop: 4 }}>
      {renderStepItem(
        {
          pending: 'Step 1: Requesting S3 link...',
          loading: 'Step 1: Requesting S3 link...',
          success: 'Step 1: S3 link received',
          error: 'Step 1: Failed to get S3 link',
        },
        s1,
      )}

      {showS2 &&
        renderStepItem(
          {
            pending: 'Step 2: Loading S3 playback...',
            loading: 'Step 2: Loading S3 playback...',
            success: 'Step 2: S3 playback started',
            error: 'Step 2: S3 playback failed (Codec/Format unsupported)',
          },
          s2,
        )}

      {isFallbackTriggered && (
        <>
          {renderStepItem(
            'Step 3: Switching to stream playback',
            { status: 'triggered' },
          )}

          {renderStepItem(
            {
              prefix: 'Step 4',
              loading: 'Step 4: Requesting stream...',
              pending: 'Step 4: Requesting stream...',
              success: 'Step 4: Stream link received',
              error: 'Step 4: Failed to get stream',
            },
            s4_req,
          )}

          {(s4_req.status === 'success' || s4_conn.status !== 'skipped') &&
            renderStepItem(
              {
                prefix: 'Step 5',
                loading: 'Step 5: Connecting to stream...',
                pending: 'Step 5: Connecting to stream...',
                success: 'Step 5: Stream playback started',
                error: 'Step 5: Stream playback failed',
              },
              s4_conn,
            )}

          {s5_fb && (
            <>
              {renderStepItem(
                'Step 6: Switching to compatible stream (H.264)',
                s5_fb,
              )}

              {s6_req &&
                renderStepItem(
                  {
                    prefix: 'Step 7',
                    loading: 'Step 7: Requesting transcode stream...',
                    pending: 'Step 7: Requesting transcode stream...',
                    success: 'Step 7: Transcode stream link received',
                    error: 'Step 7: Failed to get transcode stream',
                  },
                  s6_req,
                )}

              {s6_conn &&
                renderStepItem(
                  {
                    prefix: 'Step 8',
                    loading: 'Step 8: Connecting transcode playback...',
                    pending: 'Step 8: Connecting transcode playback...',
                    success: 'Step 8: Transcode playback ready',
                    error: 'Step 8: Transcode playback failed',
                  },
                  s6_conn,
                )}
            </>
          )}
        </>
      )}

      {isS3Success &&
        renderStepItem('Step 3: Playback is ready', {
          status: 'success',
        })}
    </div>
  );
};

export default PlaybackDebugMonitor;
